/**
 * JSON 树形视图的一个节点；对象和数组可点击展开/折叠
 */
import { useState } from 'react'
import { CaretRightOutlined } from '@ant-design/icons'
import styles from './JsonViewer.module.css'

type ValueType = 'null' | 'undefined' | 'array' | 'object' | 'string' | 'number' | 'boolean' | 'other'

function valueTypeOf(value: unknown): ValueType {
  if (value === null) return 'null'
  if (value === undefined) return 'undefined'
  if (Array.isArray(value)) return 'array'
  const type = typeof value
  if (type === 'object' || type === 'string' || type === 'number' || type === 'boolean') return type
  return 'other'
}

const TYPE_LABELS: Record<ValueType, string> = {
  null: 'Null',
  undefined: 'Undefined',
  array: '',
  object: '',
  string: 'String',
  number: 'Number',
  boolean: 'Boolean',
  other: 'Unknown'
}

interface JsonTreeNodeProps {
  data: unknown
  nodeKey?: string
  root?: boolean
  // 父级「全部展开/折叠」切换时，子节点跟随
  expanded: boolean
}

export function JsonTreeNode({ data, nodeKey, root = false, expanded }: Readonly<JsonTreeNodeProps>) {
  // 以父级的 expanded 为初值；父级切换时通过 key 重新挂载
  const [open, setOpen] = useState(expanded)
  const type = valueTypeOf(data)
  const expandable = type === 'array' || type === 'object'
  const entries = expandable ? Object.entries(data as object) : []

  let indicator = TYPE_LABELS[type]
  if (type === 'array') indicator = `[Array(${entries.length})]`
  else if (type === 'object') indicator = `{Object(${entries.length})}`

  let formatted = String(data)
  if (type === 'string') formatted = `"${data as string}"`
  else if (expandable) formatted = ''
  const propertyWord = entries.length === 1 ? 'property' : 'properties'
  const lengthText = type === 'array' ? `${entries.length} items` : `${entries.length} ${propertyWord}`

  const label = (
    <>
      {!root && <span className={styles.key}>&quot;{nodeKey}&quot;:</span>}
      <span className={styles.type}>{indicator}</span>
    </>
  )

  return (
    <div className={styles.treeNode}>
      {expandable ? (
        // 对象和数组的行是原生按钮：可点击，也可用键盘 Enter/空格展开折叠
        <button
          type="button"
          className={styles.treeItemExpandable}
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          <CaretRightOutlined className={open ? styles.toggleOpen : styles.toggle} />
          {label}
          {!open && <span className={styles.length}>{lengthText}</span>}
        </button>
      ) : (
        <div className={styles.treeItem}>
          {label}
          <span className={styles[`value_${type}`]}>{formatted}</span>
        </div>
      )}
      {expandable && open && (
        <div className={styles.children}>
          {entries.map(([key, child]) => (
            <JsonTreeNode key={key} nodeKey={key} data={child} expanded={expanded} />
          ))}
        </div>
      )}
    </div>
  )
}
